import { createContext, useContext, useEffect } from 'react'
import { useOrganization } from '../hooks/useOrganization'

const DEFAULT_PRIMARY = '#0F172A'
const DEFAULT_ACCENT = '#3B82F6'

/**
 * Derive a slightly darker variant of a hex color for hover/dark states.
 * Reduces each RGB channel by the given percentage (0-100).
 */
function darkenHex(hex: string, percent: number): string {
  const clean = hex.replace('#', '')
  if (clean.length !== 6) return hex
  const factor = 1 - percent / 100
  const r = Math.round(parseInt(clean.slice(0, 2), 16) * factor)
  const g = Math.round(parseInt(clean.slice(2, 4), 16) * factor)
  const b = Math.round(parseInt(clean.slice(4, 6), 16) * factor)
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
}

interface BrandingContextValue {
  logoUrl: string | null
  primaryColor: string
  accentColor: string
  orgName: string
}

const BrandingContext = createContext<BrandingContextValue>({
  logoUrl: null,
  primaryColor: DEFAULT_PRIMARY,
  accentColor: DEFAULT_ACCENT,
  orgName: '',
})

export function useBranding(): BrandingContextValue {
  return useContext(BrandingContext)
}

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const { org } = useOrganization()

  const primaryColor = org?.ui_primary_color ?? DEFAULT_PRIMARY
  const accentColor = org?.ui_accent_color ?? DEFAULT_ACCENT
  const logoUrl = org?.logo_url ?? null
  const orgName = org?.name ?? 'Content Engine'

  useEffect(() => {
    const root = document.documentElement
    root.style.setProperty('--color-primary', primaryColor)
    root.style.setProperty('--color-primary-dark', darkenHex(primaryColor, 10))
    root.style.setProperty('--color-accent', accentColor)
  }, [primaryColor, accentColor])

  return (
    <BrandingContext.Provider value={{ logoUrl, primaryColor, accentColor, orgName }}>
      {children}
    </BrandingContext.Provider>
  )
}
